import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Alert
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";

import Layout from "../../layout/layout";
import { api } from "../../services/api";
import TemplateLoaderPopup from "../../components/ui/TemplateLoaderPopup";
import SubmissionProgressPopup from "../../components/ui/progressPopup";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DataGrid } from '@mui/x-data-grid';
import RoleGuard from "../../components/RoleGuard";

const Bulktags = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const resourceId = 81;
  const accountid = sessionStorage.getItem("activeAccountId");
  const userId = sessionStorage.getItem('userId');

  // State
  const [activeStep, setActiveStep] = useState(0); // 0 = Upload, 1 = Review
  const [fields, setFields] = useState([]);
  const [files, setFiles] = useState([]);
  const [validationData, setValidationData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Loader & Progress State
  const [isLoadingValidation, setIsLoadingValidation] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [loadingTitle, setLoadingTitle] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);

  const [submissionOpen, setSubmissionOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [successMessage, setSuccessMessage] = useState("");
  const [provisionSteps, setProvisionSteps] = useState({
    integration: { status: "pending", desc: "" },
    provisioning: { status: "pending", desc: "" }
  });

  const thSx = { padding: "10px", textAlign: "center", borderBottom: "1px solid", borderColor: "divider", fontWeight: 600, fontSize: "14px" };
  const tdCenterSx = { padding: "10px", textAlign: "center", borderBottom: "1px solid", borderColor: "divider", fontSize: "13px" };
  const tdLeftSx = { padding: "10px", textAlign: "center", borderBottom: "1px solid", borderColor: "divider", fontSize: "13px", wordBreak: "break-word" };

  useEffect(() => {
    async function fetchForm() {
      try {
        const res = await api.get(`/api/Resources/form/${resourceId}/${accountid}`);
        setFields(res.data || []);
      } catch (err) {
        console.error(err);
      }
    }
    if (resourceId && accountid) fetchForm();
  }, [resourceId, accountid]);

  const downloadField = fields.find(f => f.fieldType === "button");
  const uploadField = fields.find(f => f.fieldType === "file");

  const handleFiles = (selectedFiles) => {
    if (!selectedFiles?.[0]) return;
    setFiles([selectedFiles[0]]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDownload = async (url) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([res.data]));
      link.download = "template.xlsx";
      link.click();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpload = async (field) => {
    if (!files.length) return alert("Upload file first");

    try {
      setOpenPopup(true);
      setIsLoadingValidation(true);
      setLoadingTitle("Validating Bulk Upload");
      setLoadingMessage("Uploading file...");
      setLoadingProgress(20);

      const formData = new FormData();
      formData.append("file", files[0]);

      setTimeout(() => {
        setLoadingMessage("Validating rows...");
        setLoadingProgress(80);
      }, 600);

      const res = await api.post(field.dropdownSource, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { resourceId, accountId: accountid, userId },
      });

      setValidationData(res.data);
      setLoadingProgress(100);
    } catch (e) {
      console.error(e);
      toast.error("Validation failed to run.");
    } finally {
      setTimeout(() => setIsLoadingValidation(false), 500);
    }
  };

  const handleSubmit = async () => {
    if (!validationData?.isValid) return;

    try {
      setSubmissionOpen(true);
      setProgress(20);
      setProvisionSteps({
        integration: { status: "loading", desc: "Submitting batch..." },
        provisioning: { status: "pending", desc: "Waiting..." }
      });

      const payload = {
        bulkBatchId: validationData.bulkBatchId,
        resourceId, accountId: accountid, userId
      };

      const res = await api.post("/api/AwsEc2/BulkEC2Upload", payload, {
        headers: { "X-CORRELATION-ID": crypto.randomUUID() }
      });

      setProvisionSteps({
        integration: { status: "success", desc: "Batch accepted" },
        provisioning: { status: "success", desc: "Resources created" }
      });

      setSuccessMessage(res.data.message || "Success");
      setProgress(100);
      toast.success(res.data?.message || "Request submitted successfully");

      setTimeout(() => {
        setSubmissionOpen(false);
        navigate("/provision-status");
      }, 4000);

    } catch (err) {
      console.error(err);
      setProvisionSteps({
        integration: { status: "success", desc: "Batch validated" },
        provisioning: { status: "error", desc: err?.response?.data?.message || "Failed" }
      });
      setProgress(100);
      toast.error(err?.response?.data?.message || "Submission failed ");
    }
  };

  const allRows = validationData?.rows || [];
  const failedRows = allRows.filter((r) => r.validationResult === "Fail");
  const passedRows = allRows.filter((r) => r.validationResult.toLowerCase() === "pass");
  const handleRemoveFile = (e) => {
    if (e) e.stopPropagation();
    setFiles([]);
    setValidationData(null);
    setActiveStep(0);

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const passedColumns = [
    {
      field: 'rowNumber',
      headerName: 'Row',
      width: 60,
      align: 'left',
      headerAlign: 'left'
    },
    {
      field: 'generatedFinalResourceName',
      headerName: 'Resource Name',
      flex: 1.5,
      minWidth: 220,
      align: 'left',
      headerAlign: 'left'
    },
    {
      field: 'validationResult',
      headerName: 'Status',
      width: 90,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <span style={{ color: theme.palette.success.main, fontWeight: 600 }}>{params.value}</span>
      )
    },
    {
      field: 'comment',
      headerName: 'Comment',
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => params.row.comment || "-"
    },
    {
      field: 'warningMessage',
      headerName: 'Warning',
      flex: 2,
      minWidth: 300,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <span style={{ color: theme.palette.warning.main, fontWeight: 500 }}>
          {params.row.isWarning ? params.row.warningMessage : "-"}
        </span>
      )
    },
    { field: 'estimatedMonthlyCost', headerName: 'Est Cost', width: 100, align: 'left', headerAlign: 'left' },
    { field: 'forecastedMonthlyCost', headerName: 'Forecast Cost', width: 110, align: 'left', headerAlign: 'left' },
  ];

  const warningCount = passedRows.filter(r => r.isWarning).length;
  const isSingularWarning = warningCount === 1;

  return (
    <Layout pageType="sideBar">
      <TemplateLoaderPopup
        variant="modern"
        isLoading={isLoadingValidation}
        open={openPopup && (isLoadingValidation || validationData)}
        title={loadingTitle}
        message={loadingMessage}
        progress={loadingProgress}
        stats={validationData && !isLoadingValidation ? { total: validationData.totalRows, passed: validationData.passedRows, failed: validationData.failedRows } : null}
        showActions={!isLoadingValidation}
        isValid={validationData?.isValid}
        onViewResults={() => {
          setOpenPopup(false);
          if (validationData?.totalRows === 0) {
            setFiles([]);
            if (fileInputRef.current) fileInputRef.current.value = null;
            return;
          }
          setActiveStep(1);
        }}
        onSubmit={() => {
          setOpenPopup(false);
          handleSubmit();
        }}
      />

      <SubmissionProgressPopup
        open={submissionOpen}
        progress={progress}
        steps={provisionSteps}
        successMessage={successMessage}
      />

      <Paper sx={{ p: 4, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Typography variant="title" mb={2} color="text.primary">
          Bulk EC2 Provisioning
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 4, mt: 2, alignItems: "center" }}>
          <Chip
            icon={files.length ? <CheckCircleIcon /> : undefined}
            label="Template Upload"
            color={files.length ? "success" : "default"}
            size="small"
            variant="outlined"
            sx={{
              border: "none",
              fontWeight: files.length ? 600 : 400
            }}
          />

          <Chip
            icon={validationData ? <CheckCircleIcon /> : undefined}
            label="Validation"
            color={validationData ? "success" : "default"}
            size="small"
            variant="outlined"
            sx={{
              border: "none",
              fontWeight: validationData ? 600 : 400
            }}
          />

          <Chip
            icon={activeStep === 1 && validationData?.failedRows === 0 ? <CheckCircleIcon /> : undefined}
            label="Ready for Provisioning"
            color={activeStep === 1 && validationData?.failedRows === 0 ? "primary" : "default"}
            size="small"
            variant="outlined"
            sx={{
              border: "none",
              fontWeight: activeStep === 1 && validationData?.failedRows === 0 ? 600 : 400
            }}
          />
        </Box>

        {/* --- VIEW 1: UPLOAD --- */}
        {activeStep === 0 && (
          <Box
            maxWidth="80%"
            mx="auto"
            sx={{
              maxHeight: "calc(100vh - 260px)",
              overflowY: "auto",
              overflowX: "hidden",
              px: 1,
              scrollbarGutter: "stable",

              "&::-webkit-scrollbar": {
                width: "4px",
                backgroundColor: "transparent",
              },

              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "transparent",
                borderRadius: "8px",
              },

              "&:hover::-webkit-scrollbar-thumb": {
                backgroundColor: "#bdbdbd",
              },

              "&:hover::-webkit-scrollbar-thumb:active": {
                backgroundColor: "#9e9e9e",
              },
            }}
          >
            {fields.length === 0 && (
              <Typography color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                Loading form configuration...
              </Typography>
            )}

            <Paper
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden', mb: 4, bgcolor: 'background.paper' }}
            >
              <Box
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  p: 2, cursor: "pointer",
                  bgcolor: files.length
                    ? (isDark ? 'rgba(76, 175, 80, 0.08)' : '#f0fdf4')
                    : (isDark ? 'rgba(255, 255, 255, 0.02)' : 'grey.50'),
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  minHeight: "180px", borderBottom: '1px dashed', borderColor: files.length ? "success.main" : "divider",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: files.length
                      ? (isDark ? 'rgba(76, 175, 80, 0.15)' : '#dcfce7')
                      : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'grey.100')
                  }
                }}
              >
                <input ref={fileInputRef} type="file" hidden accept=".xlsx, .xls" onChange={(e) => handleFiles(e.target.files)} />

                {!files.length ? (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 80, color: "primary.main", mb: 2, opacity: 0.8 }} />
                    <Typography variant="h5" color="text.primary" fontWeight="600" gutterBottom>Drag and drop your template here</Typography>
                    <Typography color="text.secondary" variant="body1">or click to browse from your computer</Typography>
                    <Typography color="text.secondary" variant="caption" sx={{ mt: 2, display: 'block' }}>Supports .xlsx files only</Typography>
                  </>
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <CloudUploadIcon sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
                    <Typography variant="h6" color="success.main" fontWeight="600" gutterBottom>File ready for validation</Typography>
                    <Chip
                      size="large" label={files[0].name} color="success" variant="outlined"
                      onDelete={handleRemoveFile}
                      deleteIcon={<CloseIcon />} sx={{ px: 2, py: 3, fontSize: "1.1rem", mt: 2, bgcolor: isDark ? 'background.default' : 'white' }}
                    />
                  </Box>
                )}
              </Box>

              {/* --- HELPER TOOLS FOOTER --- */}
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'grey.50', borderTop: "1px dashed", borderColor: "divider" }}>

                {/* Step 1: Download Template */}
                <Box sx={{ p: 3, display: 'flex', flex: 1, borderRight: { sm: "1px dashed" }, justifyContent: 'space-between', alignItems: 'center', borderColor: "divider" }}>
                  <Box sx={{ maxWidth: '60%' }}>
                    <Typography variant="subtitle1" color="text.primary" fontWeight="600">
                      1. Need the template?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Download the required Excel schema to ensure your data validates correctly.
                    </Typography>
                  </Box>
                  <Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled={!downloadField}
                      onClick={() => downloadField && handleDownload(downloadField.dropdownSource)}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      {downloadField ? "Download Template" : "Loading..."}
                    </Button>
                  </Box>
                </Box>

                {/* Step 2: Master Data Dictionary */}
                <Box sx={{ p: 3, display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ maxWidth: '60%' }}>
                    <Typography variant="subtitle1" color="text.primary" fontWeight="600">
                      2. Need Resource IDs?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Search the dictionary to copy valid VPC, Subnet, and AMI IDs for your file.
                    </Typography>
                  </Box>
                  <Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{ textTransform: "none", fontWeight: 600 }}
                      disableElevation
                      onClick={() => navigate('/awsMasterData')}
                    >
                      Open Dictionary
                    </Button>
                  </Box>
                </Box>

              </Box>
            </Paper>

<RoleGuard requiredPermission="BULK_EC2">
            <Box display="flex" justifyContent="center">
              <Button
                size="large" variant="contained" color="primary" onClick={() => handleUpload(uploadField)} disabled={files.length === 0}
                sx={{ px: 8, py: 1.5, fontWeight: 600, borderRadius: 2, textTransform: 'none', boxShadow: files.length ? 3 : 0 }}
              >
                Validate Data
              </Button>
            </Box>
            </RoleGuard>
          </Box>
        )}

        {/* --- VIEW 2: REVIEW & PROVISION --- */}
        {activeStep === 1 && validationData && (
          <Box>

            {/* COMPACT STATS BAR */}
            <Paper
              elevation={0}
              sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', p: 1.5, mb: 2, bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
            >
              <Box display="flex" gap={3} alignItems="center">
                <Typography variant="body2" color="text.secondary" fontWeight={600}>Row Summary:</Typography>
                <Typography variant="body2" color="text.primary">Total: <strong>{validationData.totalRows}</strong></Typography>
                <Typography variant="body2" color="success.main">Passed: <strong>{validationData.passedRows}</strong></Typography>
                <Typography variant="body2" color="error.main">Failed: <strong>{validationData.failedRows}</strong></Typography>
              </Box>

              <Box display="flex" gap={4} alignItems="center">
                <Typography variant="body2" display="flex" alignItems="center" color="text.primary">
                  <span style={{ color: isDark ? '#aaa' : '#666', marginRight: '8px' }}>Est. Monthly:</span><strong>${validationData.totalEstimatedMonthlyCost}</strong>
                </Typography>
                <Typography variant="body2" display="flex" alignItems="center" color="text.primary">
                  <span style={{ color: isDark ? '#aaa' : '#666', marginRight: '8px' }}>Forecasted:</span><strong>${validationData.totalForecastedMonthlyCost}</strong>
                </Typography>
              </Box>
            </Paper>

            {/* FAILED STATE */}
            {validationData.failedRows > 0 ? (
              <Box>
                <Typography variant="subtitle1" color="error.main" mb={2} fontWeight="600">Action Required: Fix Errors</Typography>

                <Box sx={{ maxHeight: "calc(100vh - 420px)", overflowY: "auto", border: "1px solid", borderColor: isDark ? "error.dark" : "#fca5a5", borderRadius: "6px", mb: 2 }}>
                  <Box component="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <Box component="thead" style={{ position: "sticky", top: 0, zIndex: 1 }} sx={{ bgcolor: isDark ? 'rgba(211, 47, 47, 0.16)' : '#fef2f2', borderBottom: "1px solid", borderColor: isDark ? 'error.main' : '#fca5a5' }}>
                      <Box component="tr">
                        <Box component="th" sx={{ ...thSx, color: isDark ? 'error.light' : '#991b1b' }}>Row</Box>
                        <Box component="th" sx={{ ...thSx, color: isDark ? 'error.light' : '#991b1b', textAlign: "left" }}>Resource Name</Box>
                        <Box component="th" sx={{ ...thSx, color: isDark ? 'error.light' : '#991b1b', textAlign: "left" }}>Error Description</Box>
                        <Box component="th" sx={{ ...thSx, color: isDark ? 'error.light' : '#991b1b', textAlign: "left" }}>Warning</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {failedRows.map((r, i) => (
                        <Box component="tr" key={i} sx={{ backgroundColor: i % 2 === 0 ? (isDark ? 'background.paper' : '#ffffff') : (isDark ? 'rgba(211, 47, 47, 0.05)' : '#fef2f2') }}>
                          <Box component="td" sx={{ ...tdCenterSx, color: isDark ? 'error.light' : '#991b1b', fontWeight: 600 }}>{r.rowNumber}</Box>
                          <Box component="td" sx={{ ...tdLeftSx, textAlign: "left", color: isDark ? 'error.light' : '#991b1b', fontWeight: 500 }}>{r.generatedFinalResourceName || "-"}</Box>
                          <Box component="td" sx={{ ...tdLeftSx, textAlign: "left", color: isDark ? 'error.main' : '#dc2626' }}>{r.comment}</Box>
                          <Box component="td" sx={{ ...tdLeftSx, textAlign: "left", color: isDark ? 'warning.main' : '#d97706' }}>{r.isWarning ? r.warningMessage : "-"}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" justifyContent="flex-start">
                  <Button variant="outlined" onClick={handleRemoveFile}>Back to Upload</Button>
                </Box>
              </Box>
            ) : (
              /* PASSED STATE */
              <Box>
                <Typography variant="subtitle1" color="success.main" mb={1} fontWeight="600">All Validations Passed</Typography>

                <Box
  sx={{
    height: "calc(100vh - 470px)",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
  }}
>
                  <DataGrid
                    rows={passedRows.map((r, i) => ({ ...r, id: r.rowNumber || i }))}
                    columns={passedColumns}
                    rowHeight={52}
                    hideFooter
                    disableRowSelectionOnClick

                    sx={{
                      backgroundColor: 'background.paper',
                      borderRadius: "6px",
                      border: "none",
                      '& .MuiDataGrid-cell': {
                        fontSize: '13px',
                        borderBottom: '1px solid',
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#eee',
                        color: 'text.primary',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        padding: '8px',
                      },

                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  />
                </Box>

                {/* WARNING BANNER & ACTION BUTTONS */}
                <Box>
                  {validationData.isWarningExists && (
                    <Alert
                      severity="warning"
                      sx={{
                        mt: 1.5,
                        bgcolor: isDark ? 'rgba(255, 152, 0, 0.16)' : '#fff3cd',
                        color: "text.primary",
                        border: "1px solid",
                        borderColor: isDark ? 'rgba(255, 152, 0, 0.3)' : '#ffe69c',
                        "& .MuiAlert-icon": {
                          color: isDark ? "warning.main" : "#d97706",
                        }
                      }}
                    >
                      <strong>{isSingularWarning ? "Duplicate Name Detected:" : "Duplicate Names Detected:"}</strong> We have automatically appended {isSingularWarning ? "a suffix to resolve the naming conflict" : "suffixes to resolve naming conflicts"}, <strong>as shown in the table above</strong>. If you do not want {isSingularWarning ? "this suffix" : "these suffixes"}, click <strong>"Back to Upload"</strong> to fix your file and re-upload it. Otherwise, click <strong>"Provision Resources"</strong> to proceed.
                    </Alert>
                  )}

                  <Box display="flex" justifyContent="space-between" marginTop={'15px'}>
                    <Button variant="outlined" onClick={() => setActiveStep(0)}>Back to Upload</Button>
                    <Button variant="contained" color="success" onClick={handleSubmit}>Provision Resources</Button>
                  </Box>
                </Box>

              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Layout>
  );
};

export default Bulktags;